/**
 * templateAssembler.ts — patch a fitted MappingPlan into a template slide and
 * insert it into the deck.
 *
 * POST /v1/deckorate/slides/create-slide-template returns a per-slide
 * MappingPlan, NOT a .pptx: `placeholder_assignments` keyed by SYMBOLIC slot
 * ids ("title#0", "body#1"). Assembly is deliberately the client's job — the
 * add-in already has to fetch the template's own .pptx to insert it, so it
 * patches the text in on the way through and PowerPoint receives a finished
 * slide with the template's real fonts, colours, bullets and graphics intact.
 *
 * The fetching, carving and inserting are NOT done here: they belong to
 * shared/services/pptxTemplateSlideInsert.ts, which Slide Templates and
 * Presentation Templates already insert through. This module contributes only
 * the part that is specific to create-slides — turning a MappingPlan into
 * rewritten slide XML — and hands it to that service as its `patchSlideXml`
 * hook.
 *
 * SLOT IDS ARE POSITIONAL — THE SORT MUST MATCH THE BACKEND EXACTLY
 * A slot id is "<role>#<idx>" where idx is the per-role running index AFTER
 * sorting the template's placeholders into visual reading order.
 * `sortPlaceholdersByReadingOrder` below is a port of DeckorateAPI's
 * `_sort_placeholders_by_reading_order` (services/create_slides/
 * slot_manifest.py), which is itself a port of the original add-in
 * `templateAssembler.js`. All three must agree or fitted text lands in the
 * wrong shape — a silent, plausible-looking corruption, not a crash. Do not
 * "improve" the ordering here independently of the backend.
 *
 * Crucially, the sort runs over the SAME INPUT the backend used: the
 * placeholders from the template's signature manifest entry (see
 * service/templateCatalogApi.ts), not over the live .pptx. Same input plus
 * the same algorithm is what makes the ids line up; re-deriving roles from
 * the slide XML would be a second, independently-driftable classifier.
 *
 * The slot's placeholder then names the shape (`name`, the OOXML
 * `<p:cNvPr name="...">`) and where it sits (`bbox`), which is how a slot id
 * is resolved to a physical shape in the slide XML.
 *
 * TEXT REWRITING PRESERVES THE TEMPLATE'S STYLING
 * Only the text runs are replaced. The shape's `<a:bodyPr>`/`<a:lstStyle>`,
 * the first paragraph's `<a:pPr>` (indent/bullet/alignment) and the first
 * run's `<a:rPr>` (font/size/colour) are cloned onto every new paragraph, so
 * a rewritten slide still looks like the reviewed layout it came from.
 */

import { fetchSourceBuffer, insertTemplateSlides } from "../../shared/services/pptxTemplateSlideInsert";
import { logSlideTemplatesError } from "../../slide-templates/services/slideTemplatesApi";
import {
  DEFAULT_SLIDE_DIMENSIONS,
  type CatalogPlaceholder,
  type SlideDimensionsEmu,
} from "./templateCatalogApi";
import type { PlaceholderAssignment, RichParagraph } from "./templatesApi";

const DRAWING_NS = "http://schemas.openxmlformats.org/drawingml/2006/main";
const PRESENTATION_NS = "http://schemas.openxmlformats.org/presentationml/2006/main";
const XML_NS = "http://www.w3.org/XML/1998/namespace";

/** Row-band height as a fraction of slide height, for the reading-order
 *  sort's first tiebreak. Mirrors the backend's `slide_h * 0.05`. */
const ROW_BUCKET_FRACTION = 0.05;

/* ── Reading-order sort (port — see module header) ── */

/**
 * Trailing numeric suffix in a shape name ("Deck_Content_3" → 3), else
 * Infinity so unnumbered names sort last. Port of `_name_suffix_num`.
 *
 * This tiebreak exists because grouped multi-body templates report identical
 * bboxes for several shapes (the signature's bbox extraction doesn't apply
 * the group transform), so position alone can't order them — but their names
 * do encode left-to-right position.
 */
export function nameSuffixNum(name: string | null): number {
  if (!name) return Number.POSITIVE_INFINITY;
  const match = /(?:[_\s]|^)(\d+)\s*$/.exec(name.trim());
  return match?.[1] ? Number.parseInt(match[1], 10) : Number.POSITIVE_INFINITY;
}

/**
 * Sorts placeholders top-to-bottom, left-to-right with the backend's exact
 * four tiebreaks: 5%-of-slide-height row band, then x, then name suffix,
 * then name.
 *
 * NOTE: the row band uses `Math.round`, matching the original JS assembler
 * this whole chain ports from. DeckorateAPI's Python port uses `round()`,
 * which breaks an exact .5 tie to even rather than up — reachable only when
 * a placeholder's y falls precisely on a half-band boundary.
 */
export function sortPlaceholdersByReadingOrder(
  placeholders: readonly CatalogPlaceholder[],
  slideDimensions: SlideDimensionsEmu = DEFAULT_SLIDE_DIMENSIONS,
): CatalogPlaceholder[] {
  const slideHeight = slideDimensions.cy || DEFAULT_SLIDE_DIMENSIONS.cy;
  const bucketSize = slideHeight * ROW_BUCKET_FRACTION;

  const keyed = placeholders.map((placeholder, stableIndex) => ({
    placeholder,
    stableIndex,
    rowBucket: bucketSize ? Math.round((placeholder.bbox?.y ?? 0) / bucketSize) : 0,
    x: placeholder.bbox?.x ?? 0,
    suffix: nameSuffixNum(placeholder.name),
    name: placeholder.name ?? "",
  }));

  keyed.sort((a, b) => {
    if (a.rowBucket !== b.rowBucket) return a.rowBucket - b.rowBucket;
    if (a.x !== b.x) return a.x - b.x;
    if (a.suffix !== b.suffix) return a.suffix - b.suffix;
    if (a.name !== b.name) return a.name < b.name ? -1 : 1;
    // Array.prototype.sort is stable in every supported engine, but being
    // explicit keeps the comparator a total order.
    return a.stableIndex - b.stableIndex;
  });

  return keyed.map((entry) => entry.placeholder);
}

/**
 * Slot id → the placeholder it refers to, for one template entry. Both text
 * and non-text placeholders share ONE per-role counter (as the backend's
 * `role_idx` does), so an image placeholder still consumes an index — leaving
 * it out here would shift every later id of the same role.
 */
export function buildSlotMap(
  placeholders: readonly CatalogPlaceholder[],
  slideDimensions: SlideDimensionsEmu = DEFAULT_SLIDE_DIMENSIONS,
): Map<string, CatalogPlaceholder> {
  const ordered = sortPlaceholdersByReadingOrder(placeholders, slideDimensions);
  const roleIndex = new Map<string, number>();
  const slots = new Map<string, CatalogPlaceholder>();

  for (const placeholder of ordered) {
    const index = roleIndex.get(placeholder.role) ?? 0;
    roleIndex.set(placeholder.role, index + 1);
    slots.set(`${placeholder.role}#${index}`, placeholder);
  }
  return slots;
}

/* ── Slide XML shape resolution ──────────────────── */

interface ShapeRef {
  readonly element: Element;
  readonly name: string;
  readonly x: number;
  readonly y: number;
}

function childByLocalName(parent: Element, ns: string, localName: string): Element | null {
  for (const child of Array.from(parent.children)) {
    if (child.localName === localName && child.namespaceURI === ns) return child;
  }
  return null;
}

function shapeOffset(sp: Element): { x: number; y: number } {
  const spPr = childByLocalName(sp, PRESENTATION_NS, "spPr");
  const xfrm = spPr ? childByLocalName(spPr, DRAWING_NS, "xfrm") : null;
  const off = xfrm ? childByLocalName(xfrm, DRAWING_NS, "off") : null;
  return {
    x: Number.parseInt(off?.getAttribute("x") ?? "0", 10) || 0,
    y: Number.parseInt(off?.getAttribute("y") ?? "0", 10) || 0,
  };
}

/**
 * Every `<p:sp>` in the slide, INCLUDING those nested inside `<p:grpSp>` —
 * grouped shapes are exactly the case the name-suffix tiebreak exists for, so
 * they must be resolvable here too.
 */
function collectShapes(doc: XMLDocument): ShapeRef[] {
  const shapes: ShapeRef[] = [];
  for (const sp of Array.from(doc.getElementsByTagNameNS(PRESENTATION_NS, "sp"))) {
    const nvSpPr = childByLocalName(sp, PRESENTATION_NS, "nvSpPr");
    const cNvPr = nvSpPr ? childByLocalName(nvSpPr, PRESENTATION_NS, "cNvPr") : null;
    const offset = shapeOffset(sp);
    shapes.push({ element: sp, name: cNvPr?.getAttribute("name") ?? "", ...offset });
  }
  return shapes;
}

/**
 * The shape a slot's placeholder refers to. Matching is by shape NAME, with
 * the placeholder's bbox breaking a tie when a template repeats one name
 * (grouped shapes do). `used` stops two slots from both writing into the same
 * shape — which would silently drop one slot's content.
 */
function resolveShape(
  shapes: readonly ShapeRef[],
  placeholder: CatalogPlaceholder,
  used: ReadonlySet<Element>,
): ShapeRef | null {
  const candidates = shapes.filter(
    (shape) => shape.name === (placeholder.name ?? "") && !used.has(shape.element),
  );
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0] ?? null;

  const bbox = placeholder.bbox;
  if (!bbox) return candidates[0] ?? null;

  let closest = candidates[0]!;
  let closestDistance = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const distance = Math.abs(candidate.x - bbox.x) + Math.abs(candidate.y - bbox.y);
    if (distance < closestDistance) {
      closestDistance = distance;
      closest = candidate;
    }
  }
  return closest;
}

/* ── Text rewriting ──────────────────────────────── */

/** One paragraph to write: its runs, and whether it should carry a bullet. */
interface OutputParagraph {
  readonly runs: ReadonlyArray<{ text: string; bold?: boolean; italic?: boolean; underline?: boolean }>;
}

/**
 * The paragraphs an assignment wants written. `richBullets` wins when
 * present (it carries the emphasis the fitter preserved); otherwise `text` is
 * split on newlines, which is how `action: "split"` delivers multi-paragraph
 * content. Returns an empty array when there is nothing to write, so the
 * caller leaves the template's own text alone.
 */
function assignmentParagraphs(assignment: PlaceholderAssignment): OutputParagraph[] {
  if (assignment.action === "leave_blank") return [];

  const rich = assignment.richBullets;
  if (Array.isArray(rich) && rich.length > 0) {
    const paragraphs = rich
      .map((paragraph: RichParagraph) => ({
        runs: (paragraph.runs ?? []).filter((run) => typeof run.text === "string"),
      }))
      .filter((paragraph) => paragraph.runs.length > 0);
    if (paragraphs.length > 0) return paragraphs;
  }

  const text = assignment.text;
  if (typeof text !== "string" || !text.trim()) return [];
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => ({ runs: [{ text: line }] }));
}

function setEmphasis(rPr: Element, run: OutputParagraph["runs"][number]): void {
  // Only ever ADD emphasis the fitter asked for — clearing the template's own
  // bold/italic when a run doesn't mention it would strip the layout's styling.
  if (run.bold) rPr.setAttribute("b", "1");
  if (run.italic) rPr.setAttribute("i", "1");
  if (run.underline) rPr.setAttribute("u", "sng");
}

/**
 * Builds one `<a:r>` carrying `run`'s text, styled from `templateRPr` (the
 * template's own first run properties) so font/size/colour survive.
 */
function buildRun(
  doc: XMLDocument,
  run: OutputParagraph["runs"][number],
  templateRPr: Element | null,
): Element {
  const r = doc.createElementNS(DRAWING_NS, "a:r");
  if (templateRPr) {
    const rPr = templateRPr.cloneNode(true) as Element;
    setEmphasis(rPr, run);
    r.append(rPr);
  } else if (run.bold || run.italic || run.underline) {
    const rPr = doc.createElementNS(DRAWING_NS, "a:rPr");
    rPr.setAttribute("lang", "en-US");
    setEmphasis(rPr, run);
    r.append(rPr);
  }

  const t = doc.createElementNS(DRAWING_NS, "a:t");
  // Leading/trailing whitespace is significant in a run and would otherwise
  // be collapsed by consumers.
  if (run.text !== run.text.trim()) t.setAttributeNS(XML_NS, "xml:space", "preserve");
  t.textContent = run.text;
  r.append(t);
  return r;
}

/** The `<a:rPr>` of a paragraph's first run — the template's text styling. */
function templateRunProperties(paragraph: Element | null): Element | null {
  if (!paragraph) return null;
  const firstRun = childByLocalName(paragraph, DRAWING_NS, "r");
  return firstRun ? childByLocalName(firstRun, DRAWING_NS, "rPr") : null;
}

/** The template paragraph's own styling, cloned onto every paragraph
 *  `writeShapeText` writes — this is what keeps a rewritten slide looking
 *  like the reviewed layout it came from (see this module's header). */
interface TemplateParagraphStyle {
  readonly pPr: Element | null;
  readonly rPr: Element | null;
  readonly endRPr: Element | null;
}

/** One `<a:p>` carrying `paragraph`'s runs, styled per `style`. */
function buildParagraphElement(
  doc: Document,
  paragraph: OutputParagraph,
  style: TemplateParagraphStyle,
): Element {
  const p = doc.createElementNS(DRAWING_NS, "a:p");
  if (style.pPr) p.append(style.pPr.cloneNode(true));
  for (const run of paragraph.runs) p.append(buildRun(doc, run, style.rPr));
  if (style.endRPr) p.append(style.endRPr.cloneNode(true));
  return p;
}

/** One empty `<a:p>` carrying only the template's own paragraph styling — an
 *  empty `<p:txBody>` is invalid OOXML (PowerPoint refuses the whole
 *  package), so this is what a shape gets when nothing was written to it. */
function buildBlankParagraph(doc: Document, style: TemplateParagraphStyle): Element {
  const p = doc.createElementNS(DRAWING_NS, "a:p");
  if (style.pPr) p.append(style.pPr.cloneNode(true));
  if (style.endRPr) p.append(style.endRPr.cloneNode(true));
  return p;
}

/**
 * Replaces a shape's paragraphs with `paragraphs`, keeping the template's
 * `<a:bodyPr>`, `<a:lstStyle>` and the first paragraph's `<a:pPr>`.
 *
 * Returns false when the shape has no `<p:txBody>` at all (a picture or
 * table placeholder), so the caller can report the slot as unwritten rather
 * than assume success.
 */
function writeShapeText(sp: Element, paragraphs: readonly OutputParagraph[]): boolean {
  const txBody = childByLocalName(sp, PRESENTATION_NS, "txBody");
  if (!txBody) return false;
  const doc = sp.ownerDocument;

  const existingParagraphs = Array.from(txBody.children).filter(
    (child) => child.localName === "p" && child.namespaceURI === DRAWING_NS,
  );
  const templateParagraph = existingParagraphs[0] ?? null;
  const style: TemplateParagraphStyle = {
    pPr: templateParagraph ? childByLocalName(templateParagraph, DRAWING_NS, "pPr") : null,
    rPr: templateRunProperties(templateParagraph),
    endRPr: templateParagraph ? childByLocalName(templateParagraph, DRAWING_NS, "endParaRPr") : null,
  };

  for (const paragraph of existingParagraphs) paragraph.remove();

  for (const paragraph of paragraphs) {
    txBody.append(buildParagraphElement(doc, paragraph, style));
  }
  if (paragraphs.length === 0) {
    txBody.append(buildBlankParagraph(doc, style));
  }
  return true;
}

/** What one `applyPlanToSlideXml` call managed to write. */
export interface PlanApplyReport {
  readonly applied: number;
  /** Slot ids the plan named that couldn't be written, with why. */
  readonly unresolved: ReadonlyArray<{ slotId: string; reason: string }>;
}

export interface AppliedSlideXml extends PlanApplyReport {
  readonly xml: string;
}

/**
 * Patches `assignments` into `slideXml` and returns the rewritten XML.
 *
 * Never throws for an individual slot: a slot the plan names but the template
 * doesn't have (or whose shape has no text body) is reported in `unresolved`
 * and the rest of the slide is still written. A slide where NOTHING resolved
 * is still returned — the caller decides whether inserting the untouched
 * template is better than inserting nothing.
 */
export function applyPlanToSlideXml(
  slideXml: string,
  slots: ReadonlyMap<string, CatalogPlaceholder>,
  assignments: Readonly<Record<string, PlaceholderAssignment>>,
): AppliedSlideXml {
  const doc = new DOMParser().parseFromString(slideXml, "application/xml");
  if (doc.getElementsByTagName("parsererror").length > 0) {
    throw new Error("The template slide's XML could not be parsed.");
  }

  const shapes = collectShapes(doc);
  const used = new Set<Element>();
  const unresolved: Array<{ slotId: string; reason: string }> = [];
  let applied = 0;

  for (const [slotId, assignment] of Object.entries(assignments)) {
    if (!assignment || typeof assignment !== "object") continue;
    // "leave_blank" is a decision, not a failure — the template's own text is
    // meant to stay, so this isn't reported as unresolved.
    const paragraphs = assignmentParagraphs(assignment);
    if (paragraphs.length === 0) continue;

    const placeholder = slots.get(slotId);
    if (!placeholder) {
      unresolved.push({ slotId, reason: "no such slot in this template" });
      continue;
    }
    const shape = resolveShape(shapes, placeholder, used);
    if (!shape) {
      unresolved.push({ slotId, reason: `no unused shape named "${placeholder.name ?? ""}"` });
      continue;
    }
    if (!writeShapeText(shape.element, paragraphs)) {
      unresolved.push({ slotId, reason: "shape has no text body" });
      continue;
    }
    used.add(shape.element);
    applied += 1;
  }

  const xml = new XMLSerializer().serializeToString(doc);
  // XMLSerializer drops the declaration; PowerPoint wants it back.
  const withDeclaration = xml.startsWith("<?xml")
    ? xml
    : `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\r\n${xml}`;
  return { xml: withDeclaration, applied, unresolved };
}

/* ── Insertion, via the shared template-insert service ── */

export interface AssembleSlideRequest {
  /** Directory-SAS URL for the template's own `.pptx`. */
  readonly sourceFileUrl: string;
  /** 1-based slide number inside that `.pptx`. */
  readonly templateSlideIndex: number;
  /** Slot id → placeholder, from `buildSlotMap` over the signature entry. */
  readonly slots: ReadonlyMap<string, CatalogPlaceholder>;
  readonly assignments: Readonly<Record<string, PlaceholderAssignment>>;
}

const INSERT_MESSAGES = {
  noInsertableSelection: "This layout has no insertable slide content.",
  sourceDownloadFailed: "Downloading the slide template failed.",
};

const INSERT_TELEMETRY = {
  log: logSlideTemplatesError,
  reasonPrefix: "create-slide-template",
};

/**
 * Starts downloading every distinct template `.pptx` at once, into the same
 * `sourceCache` `assembleAndInsert` reads. Inserts must stay sequential (deck
 * order), but the downloads needn't — without this, slide N+1's file only
 * starts downloading after slide N has been inserted.
 *
 * Fire-and-forget: a failed download is left in the cache and surfaces as
 * that slide's own insert error, exactly as an un-prefetched one would.
 */
export function prefetchTemplateSources(
  sourceFileUrls: Iterable<string>,
  sourceCache: Map<string, Promise<ArrayBuffer>>,
): void {
  for (const url of new Set(sourceFileUrls)) {
    // The .catch is on a branch, not the cached promise: it only stops an
    // "unhandled rejection" for a file whose slide hasn't been reached yet.
    fetchSourceBuffer(url, INSERT_MESSAGES, INSERT_TELEMETRY, sourceCache).catch(() => undefined);
  }
}

/**
 * Fetches the template `.pptx`, patches the fitted content into the chosen
 * slide, and inserts it at the end of the active deck.
 *
 * The fetch / slide-carve / insert half is NOT reimplemented here: it goes
 * through shared/services/pptxTemplateSlideInsert.ts, the same path Slide
 * Templates and Presentation Templates already insert through (SAS version
 * header, one download per source file, `<p:sldIdLst>` trim that leaves the
 * layout/master/theme/media intact, base64 hand-off to
 * `insertSlidesFromBase64`). All this module adds is the `patchSlideXml` hook
 * that service now exposes — the create-slides-specific part, above.
 *
 * Called once per slide, with a shared `sourceCache`, so per-slide progress
 * and per-slide error isolation survive while a template used by several
 * slides is still downloaded only once.
 *
 * MUST only ever run in the task pane — `PowerPoint.run()` is unavailable
 * inside an Office Dialog window.
 */
export async function assembleAndInsert(
  request: AssembleSlideRequest,
  sourceCache?: Map<string, Promise<ArrayBuffer>>,
): Promise<PlanApplyReport> {
  // Captured out of the patch callback: the report is what the caller needs
  // in order to warn about slots the plan named but the template lacks.
  let report: PlanApplyReport = { applied: 0, unresolved: [] };

  const entry = {
    id: `${request.sourceFileUrl}#${request.templateSlideIndex}`,
    insertable: true,
    sourceFileUrl: request.sourceFileUrl,
    slideIndex: request.templateSlideIndex,
  };

  await insertTemplateSlides([entry], [entry.id], INSERT_MESSAGES, INSERT_TELEMETRY, {
    patchSlideXml: (slideXml) => {
      const applied = applyPlanToSlideXml(slideXml, request.slots, request.assignments);
      report = { applied: applied.applied, unresolved: applied.unresolved };
      return applied.xml;
    },
    ...(sourceCache ? { sourceCache } : {}),
  });

  return report;
}
